import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const NS = 'http://www.w3.org/2000/svg'

const POINT_XY = [
  [154, 345],
  [342, 136],
  [485, 559],
  [579, 192],
  [769, 57],
  [772, 416],
]

const TRAIL = [...POINT_XY, [945, 330]]

export class Timeline {
  constructor() {
    gsap.registerPlugin(ScrollTrigger)
    document.querySelectorAll('[data-timeline]').forEach((section) => this.#bind(section))
  }

  #bind(section) {
    const mq = window.matchMedia('(max-width: 769px), (max-height: 850px)')
    const apply = () => this.#setup(section, mq.matches)
    apply()
    mq.addEventListener('change', apply)
  }

  #kill(section) {
    const pin = section.querySelector('.s-timeline__pin') || section
    ScrollTrigger.getAll().forEach((st) => {
      if (st.trigger === pin || st.trigger === section || (st.pin && (st.pin === pin || section.contains(st.pin)))) {
        st.kill()
      }
    })
  }

  #setup(section, isPortrait) {
    this.#kill(section)

    const pin = section.querySelector('.s-timeline__pin') || section
    const milestones = this.#milestones(section)
    const els = milestones.flatMap((m) => m.els)
    const { path, draw } = this.#strokePath(section)

    if (!path || !draw) {
      gsap.set(els, { opacity: 1 })
      return
    }

    const len = draw.getTotalLength()
    const pad = 32
    const along = this.#alongPath(draw, POINT_XY)

    gsap.set(els, { opacity: 0 })
    path.setAttribute('marker-end', 'none')
    gsap.set(draw, { strokeDasharray: len + pad, strokeDashoffset: len + pad })

    const keepVisible = (self) => {
      if (self.pin) self.pin.style.overflow = 'visible'
      if (self.pin?.parentElement?.classList.contains('pin-spacer')) {
        self.pin.parentElement.style.overflow = 'visible'
      }
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: pin,
        pin: true,
        start: () => `top ${this.#pinOffset()}px`,
        end: () => `+=${Math.round(window.innerHeight * (isPortrait ? 2.4 : 1.8))}`,
        scrub: 0.35,
        snap: {
          snapTo: 'labels',
          duration: { min: 0.12, max: 0.35 },
          delay: 0.06,
          ease: 'power1.inOut',
        },
        invalidateOnRefresh: true,
        onRefresh: keepVisible,
        onEnter: keepVisible,
        onEnterBack: keepVisible,
      },
    })

    tl.addLabel('start', 0)
    tl.to(draw, { strokeDashoffset: 0, duration: 1, ease: 'none' })
    tl.set(path, { attr: { 'marker-end': 'url(#tl-arrow-tip)' } }, 0.97)

    milestones.forEach(({ els: milEls }, i) => {
      const at = along[i] * 0.97
      tl.addLabel(`p${i}`, at)
      tl.to(milEls, { opacity: 1, duration: 0.06, ease: 'none' }, at)
    })
    tl.addLabel('end', 1)
  }

  #pinOffset() {
    const bar = document.querySelector('.h-wrap .h')
    if (!bar) return 108
    return Math.ceil(bar.getBoundingClientRect().bottom) + 16
  }

  #strokePath(section) {
    const svg = section.querySelector('.s-timeline__track svg')
    if (!svg) return { path: null, draw: null }

    const d = this.#curve(TRAIL)

    if (!svg.querySelector('#tl-arrow-tip')) {
      let defs = svg.querySelector('defs')
      if (!defs) {
        defs = document.createElementNS(NS, 'defs')
        svg.insertBefore(defs, svg.firstChild)
      }
      const marker = document.createElementNS(NS, 'marker')
      marker.id = 'tl-arrow-tip'
      marker.setAttribute('viewBox', '0 0 10 10')
      marker.setAttribute('refX', '8')
      marker.setAttribute('refY', '5')
      marker.setAttribute('markerWidth', '7')
      marker.setAttribute('markerHeight', '7')
      marker.setAttribute('orient', 'auto')
      const tip = document.createElementNS(NS, 'path')
      tip.setAttribute('d', 'M0 0L10 5L0 10z')
      tip.setAttribute('fill', '#fff')
      marker.appendChild(tip)
      defs.appendChild(marker)
    }

    let mask = svg.querySelector('#tl-draw-mask')
    if (!mask) {
      let defs = svg.querySelector('defs')
      if (!defs) {
        defs = document.createElementNS(NS, 'defs')
        svg.insertBefore(defs, svg.firstChild)
      }
      mask = document.createElementNS(NS, 'mask')
      mask.id = 'tl-draw-mask'
      mask.setAttribute('maskUnits', 'userSpaceOnUse')
      const drawEl = document.createElementNS(NS, 'path')
      drawEl.setAttribute('data-draw-mask', '')
      drawEl.setAttribute('fill', 'none')
      drawEl.setAttribute('stroke', '#fff')
      drawEl.setAttribute('stroke-width', '12')
      drawEl.setAttribute('stroke-linecap', 'round')
      drawEl.setAttribute('stroke-linejoin', 'round')
      mask.appendChild(drawEl)
      defs.appendChild(mask)
    }

    const draw = mask.querySelector('[data-draw-mask]')
    draw.setAttribute('d', d)

    let path = svg.querySelector('[data-arrow-stroke]')
    if (!path) {
      path = document.createElementNS(NS, 'path')
      path.setAttribute('data-arrow-stroke', '')
      path.setAttribute('fill', 'none')
      path.setAttribute('stroke', '#fff')
      path.setAttribute('stroke-width', '2.2')
      path.setAttribute('stroke-linecap', 'round')
      path.setAttribute('stroke-linejoin', 'round')
      path.setAttribute('stroke-dasharray', '6 10')
      path.setAttribute('mask', 'url(#tl-draw-mask)')
      svg.appendChild(path)
    }

    path.setAttribute('d', d)
    return { path, draw }
  }

  // Catmull-Rom → cubics. Tension basse + clamp viewBox pour ne plus couper en haut.
  #curve(pts) {
    const t = 0.5
    const clamp = ([x, y]) => [
      Math.min(984, Math.max(20, x)),
      Math.min(637, Math.max(24, y)),
    ]
    const d = [`M${pts[0][0]},${pts[0][1]}`]
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i]
      const p1 = pts[i]
      const p2 = pts[i + 1]
      const p3 = pts[i + 2] || p2
      const c1 = clamp([p1[0] + (p2[0] - p0[0]) * t / 3, p1[1] + (p2[1] - p0[1]) * t / 3])
      const c2 = clamp([p2[0] - (p3[0] - p1[0]) * t / 3, p2[1] - (p3[1] - p1[1]) * t / 3])
      d.push(`C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`)
    }
    return d.join(' ')
  }

  #alongPath(path, pts) {
    const len = path.getTotalLength()
    if (!len) return pts.map((_, i) => i / Math.max(1, pts.length - 1))
    return pts.map((xy) => {
      let best = 0
      let bestD = Infinity
      for (let i = 0; i <= 80; i++) {
        const l = (i / 80) * len
        const p = path.getPointAtLength(l)
        const d = (p.x - xy[0]) ** 2 + (p.y - xy[1]) ** 2
        if (d < bestD) {
          bestD = d
          best = l
        }
      }
      return best / len
    })
  }

  #milestones(section) {
    return POINT_XY.map((xy, idx) => {
      const els = [
        ...section.querySelectorAll(`[data-tl-point="${idx}"]`),
        ...section.querySelectorAll(`[data-tl-label="${idx}"]`),
      ]
      return { xy, els }
    }).filter((m) => m.els.length)
  }
}
